import React, { useState } from 'react';
import type { PerimetreValue } from './PerimetreSelector';

interface PerimetreStatCardProps {
  // Resume concis affiche dans la carte (ex. "National", "2 régions · 3 dép.").
  valeur: string;
  // Detail structure pour le tooltip (regions / departements).
  detail?: PerimetreValue | null;
  // Libelle de repli (perimetre legacy / non defini).
  fallback?: string | null;
  icon: string;
  loading?: boolean;
}

// Detail riche du perimetre (badge national, ou chips regions + departements).
const renderDetail = (detail?: PerimetreValue | null, fallback?: string | null) => {
  if (detail?.type === 'national') {
    return <span className="perimetre-badge perimetre-badge--national">National — toute la France</span>;
  }
  if (detail?.type === 'zones') {
    if (detail.regions.length === 0 && detail.departements.length === 0) {
      return <span className="perimetre-detail-empty">Aucune zone définie.</span>;
    }
    return (
      <div className="perimetre-detail">
        {detail.regions.length > 0 && (
          <div className="perimetre-detail-group">
            <span className="perimetre-detail-label">Régions entières</span>
            <div className="perimetre-chips">
              {detail.regions.map(r => (
                <span key={`r${r.id}`} className="perimetre-chip perimetre-chip--region">{r.id} - {r.nom}</span>
              ))}
            </div>
          </div>
        )}
        {detail.departements.length > 0 && (
          <div className="perimetre-detail-group">
            <span className="perimetre-detail-label">Départements</span>
            <div className="perimetre-chips">
              {detail.departements.map(d => (
                <span key={`d${d.id}`} className="perimetre-chip">{d.id} - {d.nom}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  return <span className="perimetre-detail-empty">{fallback || 'Non défini'}</span>;
};

/**
 * Carte "Périmètre" de la Synthèse annuelle. Au survol, affiche le détail complet
 * (régions / départements ou National) dans un tooltip. Etat local + tooltip en
 * position fixe : pas de re-render du parent, pas de clipping par la section.
 */
const PerimetreStatCard: React.FC<PerimetreStatCardProps> = ({ valeur, detail, fallback, icon, loading }) => {
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);

  return (
    <div
      className="synthese-card synthese-card--perimetre"
      onMouseEnter={(e) => {
        // Aligne le tooltip sur le bord gauche de la carte : il s'ouvre vers la
        // droite et ne passe donc jamais sous la sidebar.
        const r = e.currentTarget.getBoundingClientRect();
        setTip({ x: r.left, y: r.bottom + 8 });
      }}
      onMouseLeave={() => setTip(null)}
    >
      <img className="synthese-card-icon" src={icon} alt="" aria-hidden="true" />
      <span className="synthese-card-titre">Périmètre géographique d'achat</span>
      {loading ? (
        <span className="synthese-card-skeleton" aria-hidden="true" />
      ) : (
        <span className="synthese-card-valeur">{valeur}</span>
      )}

      {tip && (
        <div className="perimetre-stat-tooltip" style={{ left: tip.x, top: tip.y }}>
          <div className="perimetre-stat-tooltip-title">Périmètre géographique d'achat</div>
          {renderDetail(detail, fallback)}
        </div>
      )}
    </div>
  );
};

export default PerimetreStatCard;
